import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge as BadgeUI } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft,
  Trophy,
  Star,
  Footprints,
  PackageCheck,
  Users,
  CalendarCheck,
  Mountain,
  Heart,
  Sparkles,
  Flame,
  Compass,
  Lock,
  CheckCircle2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Icon mapping
const BADGE_ICONS = {
  'footprints': Footprints,
  'package-check': PackageCheck,
  'users': Users,
  'calendar-check': CalendarCheck,
  'mountain': Mountain,
  'star': Star,
  'heart': Heart,
  'sparkles': Sparkles,
  'flame': Flame,
  'compass': Compass
};

// Color mapping
const BADGE_COLORS = {
  'green': 'from-green-500 to-emerald-600',
  'blue': 'from-blue-500 to-indigo-600',
  'purple': 'from-purple-500 to-violet-600',
  'amber': 'from-amber-500 to-orange-600',
  'emerald': 'from-emerald-500 to-teal-600',
  'orange': 'from-orange-500 to-red-600',
  'red': 'from-red-500 to-pink-600',
  'yellow': 'from-yellow-400 to-amber-500',
  'cyan': 'from-cyan-500 to-blue-600'
};

const BADGE_BG_COLORS = {
  'green': 'bg-green-500/20 text-green-500 border-green-500/30',
  'blue': 'bg-blue-500/20 text-blue-500 border-blue-500/30',
  'purple': 'bg-purple-500/20 text-purple-500 border-purple-500/30',
  'amber': 'bg-amber-500/20 text-amber-500 border-amber-500/30',
  'emerald': 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30',
  'orange': 'bg-orange-500/20 text-orange-500 border-orange-500/30',
  'red': 'bg-red-500/20 text-red-500 border-red-500/30',
  'yellow': 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
  'cyan': 'bg-cyan-500/20 text-cyan-500 border-cyan-500/30'
};

const Badges = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [badgeData, setBadgeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBadge, setSelectedBadge] = useState(null);

  const fetchBadges = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/badges/user`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setBadgeData(response.data);
    } catch (error) {
      console.error('Error fetching badges:', error);
      toast.error('Failed to load badges');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const checkAndAwardBadges = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/badges/check-and-award`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      if (response.data.newly_earned?.length > 0) {
        response.data.newly_earned.forEach(badge => {
          toast.success(`Badge Unlocked: ${badge.name}!`, {
            description: badge.description,
            duration: 5000
          });
        });
        // Refresh badges
        fetchBadges();
      }
    } catch (error) {
      console.error('Error checking badges:', error);
    }
  }, [token, fetchBadges]);

  useEffect(() => {
    fetchBadges();
    checkAndAwardBadges();
  }, [fetchBadges, checkAndAwardBadges]);

  const BadgeCard = ({ badge, earned = false }) => {
    const Icon = BADGE_ICONS[badge.icon] || Star;
    const gradientColor = BADGE_COLORS[badge.color] || 'from-gray-500 to-gray-600';
    const bgColor = BADGE_BG_COLORS[badge.color] || 'bg-muted text-muted-foreground';
    
    return (
      <button
        onClick={() => setSelectedBadge(badge)}
        className={`relative p-6 rounded-2xl border transition-all duration-300 text-left ${
          earned 
            ? 'bg-card border-border/50 hover:border-primary/50 hover:shadow-lg' 
            : 'bg-card/50 border-border/30 opacity-60 hover:opacity-80'
        }`}
        data-testid={`badge-${badge.id}`}
      >
        {/* Badge Icon */}
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${
          earned 
            ? `bg-gradient-to-br ${gradientColor} shadow-lg` 
            : 'bg-muted'
        }`}>
          {earned ? (
            <Icon className="h-8 w-8 text-white" />
          ) : (
            <Lock className="h-8 w-8 text-muted-foreground" />
          )}
        </div>
        
        {/* Badge Info */}
        <h3 className={`font-semibold mb-1 ${earned ? '' : 'text-muted-foreground'}`}>
          {badge.name}
        </h3>
        <p className="text-sm text-muted-foreground line-clamp-2">
          {badge.description}
        </p>
        
        {/* Points */}
        <div className={`mt-3 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${bgColor}`}>
          <Star className="h-3 w-3" />
          {badge.points} pts
        </div>
        
        {/* Earned checkmark */}
        {earned && (
          <div className="absolute top-4 right-4">
            <CheckCircle2 className="h-6 w-6 text-green-500" />
          </div>
        )}
        
        {/* Progress for locked badges */}
        {!earned && badge.progress !== undefined && badge.progress > 0 && (
          <div className="mt-3">
            <Progress value={badge.progress * 10} className="h-1.5" />
            <p className="text-xs text-muted-foreground mt-1">{badge.progress}/10 progress</p>
          </div>
        )}
      </button>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="pt-20 flex justify-center items-center min-h-[60vh]">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const progressPercent = badgeData ? (badgeData.badge_count / badgeData.total_available) * 100 : 0;

  return (
    <div className="min-h-screen bg-background" data-testid="badges-page">
      <Navigation />
      
      <main className="px-4 pt-[68px] pb-8 sm:px-6 sm:pt-20 sm:pb-12 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-3 sm:mb-4 -ml-2" data-testid="badges-back-btn">
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              Back
            </Button>

            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shrink-0">
                  <Trophy className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-bold leading-tight">Achievements</h1>
                  <p className="text-xs sm:text-sm text-muted-foreground">Track your outdoor journey</p>
                </div>
              </div>

              {/* Total Points */}
              <div className="inline-flex items-center gap-2 px-4 py-2 sm:px-6 sm:py-3 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 self-start">
                <Star className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500" />
                <span className="font-bold text-base sm:text-xl">{badgeData?.total_points || 0}</span>
                <span className="text-xs sm:text-sm text-muted-foreground">pts</span>
              </div>
            </div>
          </div>

          {/* Progress Overview */}
          <div className="mb-8 sm:mb-10 p-4 sm:p-6 rounded-2xl bg-card border border-border/50">
            <div className="flex items-center justify-between mb-3 gap-3">
              <h3 className="font-semibold text-sm sm:text-base">Collection Progress</h3>
              <span className="text-xs sm:text-sm text-muted-foreground shrink-0">
                {badgeData?.badge_count || 0} / {badgeData?.total_available || 0}
              </span>
            </div>
            <Progress value={progressPercent} className="h-2.5 sm:h-3" />
            <p className="mt-3 text-xs sm:text-sm text-muted-foreground">
              {progressPercent === 100
                ? "Congratulations! You've collected all badges!"
                : `Keep exploring to unlock ${(badgeData?.total_available || 0) - (badgeData?.badge_count || 0)} more badges`}
            </p>
          </div>

          {/* Earned Badges */}
          {badgeData?.earned_badges?.length > 0 && (
            <section className="mb-10 sm:mb-12">
              <div className="flex items-center gap-2.5 sm:gap-3 mb-4 sm:mb-6">
                <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-green-500" />
                <h2 className="text-lg sm:text-xl font-semibold">Earned ({badgeData.earned_badges.length})</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {badgeData.earned_badges.map((badge) => (
                  <BadgeCard key={badge.id} badge={badge} earned={true} />
                ))}
              </div>
            </section>
          )}

          {/* Locked Badges */}
          {badgeData?.locked_badges?.length > 0 && (
            <section>
              <div className="flex items-center gap-2.5 sm:gap-3 mb-4 sm:mb-6">
                <Lock className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground" />
                <h2 className="text-lg sm:text-xl font-semibold">Locked ({badgeData.locked_badges.length})</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {badgeData.locked_badges.map((badge) => (
                  <BadgeCard key={badge.id} badge={badge} earned={false} />
                ))}
              </div>
            </section>
          )}

          {/* Empty State */}
          {!badgeData?.earned_badges?.length && !badgeData?.locked_badges?.length && (
            <div className="text-center py-16">
              <Trophy className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Start Your Journey</h3>
              <p className="text-muted-foreground mb-6">
                Complete activities to earn badges and track your progress
              </p>
              <Button onClick={() => navigate('/quiz')} className="rounded-full">
                Take the Quiz
              </Button>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 text-center p-8 rounded-3xl bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20">
            <h3 className="text-2xl font-bold mb-2">Ready to Earn More?</h3>
            <p className="text-muted-foreground mb-6">
              Explore guides, attend events, and complete activities to unlock badges.
            </p>
            <div className="flex justify-center gap-4">
              <Button onClick={() => navigate('/discovery')} className="rounded-full" data-testid="explore-btn">
                <Compass className="mr-2 h-4 w-4" />
                Explore
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')} className="rounded-full" data-testid="dashboard-btn">
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Badge Detail Modal */}
      {selectedBadge && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
          onClick={() => setSelectedBadge(null)}
        >
          <div 
            className="bg-card rounded-3xl border border-border/50 p-8 max-w-sm mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const Icon = BADGE_ICONS[selectedBadge.icon] || Star;
              const gradientColor = BADGE_COLORS[selectedBadge.color] || 'from-gray-500 to-gray-600';
              const bgColor = BADGE_BG_COLORS[selectedBadge.color] || 'bg-muted';
              
              return (
                <>
                  <div className="text-center">
                    <div className={`w-24 h-24 rounded-3xl flex items-center justify-center mx-auto mb-6 ${
                      selectedBadge.earned 
                        ? `bg-gradient-to-br ${gradientColor} shadow-xl` 
                        : 'bg-muted'
                    }`}>
                      {selectedBadge.earned ? (
                        <Icon className="h-12 w-12 text-white" />
                      ) : (
                        <Lock className="h-12 w-12 text-muted-foreground" />
                      )}
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-2">{selectedBadge.name}</h3>
                    <p className="text-muted-foreground mb-4">{selectedBadge.description}</p>
                    
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${bgColor}`}>
                      <Star className="h-4 w-4" />
                      {selectedBadge.points} points
                    </div>
                    
                    {selectedBadge.earned && selectedBadge.earned_at && (
                      <p className="mt-4 text-sm text-muted-foreground">
                        Earned on {new Date(selectedBadge.earned_at).toLocaleDateString()}
                      </p>
                    )}
                    
                    {!selectedBadge.earned && (
                      <div className="mt-6 p-4 rounded-xl bg-muted/50">
                        <p className="text-sm text-muted-foreground">
                          <Lock className="h-4 w-4 inline mr-1" />
                          Complete the required action to unlock this badge
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    className="w-full mt-6 rounded-full" 
                    onClick={() => setSelectedBadge(null)}
                  >
                    Close
                  </Button>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default Badges;
